import React from 'react';
import styled from 'styled-components';
import BtnNormal from './BtnNormal';
import { colors } from '@/constants/colors';
import { currencyFormat } from '@/utils/currencyFormatter';
import { useUIStore } from '@/store/useUIStore';
import { Navigate, useNavigate } from 'react-router-dom';

interface Props {
   totalPayoutReceived: number;
   totalPayoutAvailable: number;
   totalSales: number;
   availableVouchers: number;
   soldVouchers: number;
   // Optional overrides — when present, the Withdraw + View All buttons
   // delegate to these instead of the voucher-store default behavior.
   // Lets /wifi-vouchers route its CardSales to the vendo payout flow
   // without forking the component.
   onWithdrawClick?: () => void;
   onViewAllClick?: () => void;
   // Optional label override for the Available Vouchers card. WifiVouchers
   // passes "Unredeemed Vouchers" since vendo bulk-printed codes are
   // unsold (waiting to be claimed). Voucher store keeps its default.
   availableVouchersLabel?: string;
   // When set, the unredeemed card gains a "View and Reprint" link that
   // opens the voucher inventory drawer. WifiVouchers-only; the voucher
   // store omits it (different product, no reprint concept there).
   onReprintClick?: () => void;
}

const CardSales = ({
   totalPayoutReceived,
   totalPayoutAvailable,
   totalSales,
   availableVouchers,
   soldVouchers,
   onWithdrawClick,
   onViewAllClick,
   availableVouchersLabel = 'Available Vouchers',
   onReprintClick,
}: Props) => {
   const { handleShow } = useUIStore();
   const navigate = useNavigate();

   // console.log(totalPayoutAvailable);
   return (
      <Wrapper>
         <Content>
            <Main>
               <TotalSalesWrapper>
                  <ImgWrapper>
                     <Img src="/icons/handshake.svg" />
                  </ImgWrapper>
                  <TotalSalesContent>
                     <SalesLabel>TotalSales</SalesLabel>
                     <SalesValue>{currencyFormat(totalSales, true)}</SalesValue>
                  </TotalSalesContent>
               </TotalSalesWrapper>

               <PayoutWrapper>
                  <PayoutContent>
                     <PayoutLabel>Available for Payout</PayoutLabel>
                     <PayoutValue color={colors.GRAY1}>
                        {currencyFormat(totalPayoutAvailable, true)}
                     </PayoutValue>
                     <div
                        onClick={() => {
                           if (onWithdrawClick) {
                              onWithdrawClick();
                           } else {
                              handleShow({
                                 template: 'payout',
                                 title: 'Request a Payout',
                                 data: null,
                              });
                           }
                        }}
                     >
                        <BtnNormal title="Withdraw" type="dark" size="auto" />
                     </div>
                  </PayoutContent>
                  <PayoutContent>
                     <PayoutLabel>Payout Received</PayoutLabel>
                     <PayoutValue color={colors.DANGER}>
                        {currencyFormat(totalPayoutReceived, true)}
                     </PayoutValue>
                     <div
                        onClick={() => {
                           if (onViewAllClick) onViewAllClick();
                           else navigate('/payout');
                        }}
                     >
                        <BtnNormal title="View all" type="dark" size="auto" />
                     </div>
                  </PayoutContent>
               </PayoutWrapper>
            </Main>
            <Vouchers>
               <VoucherContent color={colors.SECONDARY}>
                  <VoucherValue>{soldVouchers}</VoucherValue>
                  <VoucherLabel>Vouchers Sold</VoucherLabel>
               </VoucherContent>
               <VoucherContent color={colors.GRAY13}>
                  <VoucherValue>{availableVouchers}</VoucherValue>
                  <VoucherLabel>{availableVouchersLabel}</VoucherLabel>
                  {onReprintClick && (
                     <ReprintLink type="button" onClick={onReprintClick}>
                        View and Reprint
                        <svg
                           width="13"
                           height="13"
                           viewBox="0 0 24 24"
                           fill="none"
                           stroke="currentColor"
                           strokeWidth="2.5"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                        >
                           <line x1="5" y1="12" x2="19" y2="12" />
                           <polyline points="12 5 19 12 12 19" />
                        </svg>
                     </ReprintLink>
                  )}
               </VoucherContent>
            </Vouchers>
         </Content>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   margin-top: 1rem;
   @media (max-width: 768px) {
      margin-top: 12px;
      padding: 0 12px;
   }
`;
const Content = styled.div`
   display: grid;
   grid-template-columns: 1fr 200px;
   gap: 1rem;
   @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 10px;
   }
`;
const Main = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   padding: 1rem;
   border: 1px solid ${colors.GRAY14};
   border-radius: 8px;
   align-items: center;

   @media (max-width: 1146px) {
      grid-template-columns: 1fr;
   }

   @media (max-width: 768px) {
      padding: 14px;
      border-radius: 14px;
      gap: 14px;
      background: var(--app-surface);
   }
`;
const TotalSalesWrapper = styled.div`
   display: flex;
   align-items: center;
   gap: 2rem;
   @media (max-width: 768px) {
      gap: 12px;
      padding-bottom: 12px;
      border-bottom: 1px dashed ${colors.GRAY13};
   }
`;
const ImgWrapper = styled.div``;
const Img = styled.img`
   width: 60px;
   @media (max-width: 768px) {
      width: 44px;
   }
`;
const TotalSalesContent = styled.div``;
const SalesLabel = styled.p`
   font-size: 16px;
   font-weight: 600;
   color: ${colors.GRAY_DARK};
   @media (max-width: 768px) {
      font-size: 11px;
      font-weight: 500;
      color: ${colors.GRAY5};
      text-transform: uppercase;
      letter-spacing: 0.04em;
   }
`;
const SalesValue = styled.p`
   font-size: 34px;
   font-weight: 700;
   color: ${colors.PRIMARY};
   @media (max-width: 768px) {
      font-size: 24px;
      line-height: 1.1;
   }
`;
const PayoutWrapper = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 1rem;
   @media (max-width: 865px) {
      grid-template-columns: 1fr;
   }
   @media (max-width: 768px) {
      grid-template-columns: 1fr 1fr;
      gap: 10px;
   }
`;
const PayoutContent = styled.div`
   display: grid;
   grid-template-columns: 1fr;
   gap: 8px;
   @media (max-width: 768px) {
      gap: 4px;
      padding: 10px;
      background: ${colors.GRAY14};
      border-radius: 10px;
   }
`;
const PayoutLabel = styled.p`
   font-size: 15px;
   color: ${colors.GRAY5};
   @media (max-width: 768px) {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
   }
`;
const PayoutValue = styled.p`
   font-size: 20px;
   font-weight: 600;
   color: ${({ color }) => color};
   @media (max-width: 768px) {
      font-size: 16px;
      margin-bottom: 4px;
   }
`;
const Vouchers = styled.div`
   display: grid;
   grid-template-columns: 1fr;
   gap: 1rem;
   @media (max-width: 768px) {
      grid-template-columns: 1fr 1fr;
      gap: 10px;
   }
`;
const VoucherContent = styled.div`
   background: ${({ color }) => color};
   border-radius: 8px;
   text-align: center;
   padding: 8px;
   /* Lime tile keeps dark text; muted tile inherits theme text */
   color: ${({ color }) =>
      color === colors.SECONDARY ? '#1D1D1F' : 'var(--app-text)'};
   @media (max-width: 1146px) {
      display: grid;
      place-items: center;
   }
   @media (max-width: 768px) {
      padding: 12px 8px;
      border-radius: 12px;
   }
`;
const VoucherValue = styled.p`
   font-size: 24px;
   font-weight: 700;
   line-height: 1.2;
   color: inherit;
   @media (max-width: 768px) {
      font-size: 22px;
   }
`;
const VoucherLabel = styled.p`
   font-size: 15px;
   line-height: 1.2;
   color: inherit;
   opacity: 0.72;
   @media (max-width: 768px) {
      font-size: 12px;
      font-weight: 500;
   }
`;

export default CardSales;

const ReprintLink = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   margin-top: 6px;
   background: none;
   border: none;
   padding: 0;
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   color: ${colors.PRIMARY};
   text-decoration: underline;
   cursor: pointer;
`;
